import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { 
  FaTachometerAlt, 
  FaProjectDiagram, 
  FaMoneyBillWave, 
  FaFileInvoiceDollar, 
  FaChartBar, 
  FaUsers, 
  FaBuilding,
  FaBell,
  FaClipboardList,
  FaChevronLeft,
  FaChevronRight,
  FaCog
} from 'react-icons/fa';
import IconWrapper from './IconWrapper';

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
  onLinkClick?: () => void;
}

// Animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const SidebarContainer = styled.aside<{ $isOpen: boolean }>`
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${({ $isOpen }) => ($isOpen ? '250px' : '70px')};
  min-width: ${({ $isOpen }) => ($isOpen ? '250px' : '70px')};
  max-width: ${({ $isOpen }) => ($isOpen ? '250px' : '70px')};
  background: linear-gradient(180deg, #141340 0%, #1a1a4a 50%, #2d2d6b 100%);
  color: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  overflow: hidden;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(246, 139, 33, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '-100%')});
    width: 250px;
    box-shadow: ${({ $isOpen }) => ($isOpen ? '4px 0 20px rgba(0, 0, 0, 0.3)' : 'none')};
  }
`;

const SidebarHeader = styled.div<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $isOpen }) => ($isOpen ? 'space-between' : 'center')};
  padding: 1.5rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  z-index: 2;
  background: rgba(255, 255, 255, 0.02);
`;

const LogoContainer = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  .logo-text {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    font-weight: 700;
    font-size: 1.3rem;
    color: #F68B21;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    animation: ${fadeIn} 0.3s ease-out;
  }
`;

const Logo = styled.div`
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.2rem;
  box-shadow: 0 4px 12px rgba(246, 139, 33, 0.3);
  transition: all 0.3s ease;
  overflow: hidden;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(246, 139, 33, 0.4);
  }
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
`;

const LogoFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.2rem;
  color: white;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    color: white;
    background-color: rgba(255, 255, 255, 0.1);
    transform: scale(1.1);
  }
`;

const SidebarMenu = styled.ul`
  list-style: none;
  padding: 1rem 0;
  position: relative;
  z-index: 2;
  flex: 1;
  overflow-y: auto;
  margin: 0;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(246, 139, 33, 0.5);
    border-radius: 2px;
    
    &:hover {
      background: rgba(246, 139, 33, 0.7);
    }
  }
`;

const SidebarMenuItem = styled.li<{ isActive: boolean; isOpen: boolean }>`
  margin-bottom: 0.5rem;
  animation: ${slideIn} 0.3s ease-out;
  
      a {
      display: flex;
      align-items: center;
      padding: ${({ isOpen }) => (isOpen ? '0.75rem 1rem' : '0.75rem')};
      justify-content: ${({ isOpen }) => (isOpen ? 'flex-start' : 'center')};
      color: ${({ isActive }) => (isActive ? 'white' : 'rgba(255, 255, 255, 0.7)')};
      text-decoration: none;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      border-left: 4px solid ${({ isActive }) => (isActive ? '#F68B21' : 'transparent')};
      background: ${({ isActive }) => 
        isActive 
          ? 'linear-gradient(135deg, rgba(246, 139, 33, 0.2) 0%, rgba(246, 139, 33, 0.1) 100%)' 
          : 'transparent'
      };
      position: relative;
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: linear-gradient(180deg, #F68B21 0%, #e67c1a 100%);
      transform: scaleY(${({ isActive }) => (isActive ? 1 : 0)});
      transition: transform 0.3s ease;
    }
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: white;
      transform: translateX(4px);
      
      &::before {
        transform: scaleY(1);
      }
    }
    
    .menu-icon {
      font-size: 1.2rem;
      min-width: ${({ isOpen }) => (isOpen ? '1.5rem' : 'auto')};
      margin-right: ${({ isOpen }) => (isOpen ? '1rem' : '0')};
      transition: all 0.3s ease;
    }
    
    .menu-text {
      display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
      white-space: nowrap;
      font-weight: 500;
      animation: ${fadeIn} 0.3s ease-out;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 150px;
    }
  }
`;

const SidebarFooter = styled.div<{ isOpen: boolean }>`
  padding: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  background: rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  
  .developer-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
  }
  
  .developer-name {
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.8rem;
  }
  
  .developer-tagline {
    font-size: 0.65rem;
    color: rgba(255, 255, 255, 0.6);
    font-style: italic;
  }
  
  .copyright {
    font-size: 0.6rem;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 0.25rem;
  }
`;

const Sidebar: React.FC<SidebarProps> = ({ isOpen, toggleSidebar, onLinkClick }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [logoError, setLogoError] = React.useState(false);
  
  const handleLogoError = () => {
    setLogoError(true);
  };
  
  // Check if the current path matches the menu item path
  const isActive = (path: string) => {
    if (path === '/dashboard' && location.pathname === '/dashboard') {
      return true;
    }
    return location.pathname.startsWith(path) && path !== '/dashboard';
  };
  
  // Define menu items based on user role
  const getMenuItems = () => {
    const menuItems = [
      { path: '/dashboard', icon: <IconWrapper icon={FaTachometerAlt} className="menu-icon" />, text: 'Dashboard', roles: ['MD', 'TD', 'Project Manager', 'Accountant', 'Cashier', 'Store-Keeper (Logistics)', 'Procurement', 'Supervisor'] },
      { path: '/projects', icon: <IconWrapper icon={FaProjectDiagram} className="menu-icon" />, text: 'Projects', roles: ['MD', 'TD', 'Project Manager', 'Accountant', 'Cashier', 'Store-Keeper (Logistics)', 'Procurement', 'Supervisor'] },
      { path: '/funds', icon: <IconWrapper icon={FaMoneyBillWave} className="menu-icon" />, text: 'Fund Requests', roles: ['MD', 'TD', 'Project Manager', 'Accountant', 'Cashier', 'Store-Keeper (Logistics)', 'Procurement', 'Supervisor'] },
      { path: '/accounting', icon: <IconWrapper icon={FaFileInvoiceDollar} className="menu-icon" />, text: 'Accounting', roles: ['MD', 'TD', 'Project Manager', 'Accountant', 'Cashier', 'Store-Keeper (Logistics)', 'Procurement'] },
      { path: '/reports', icon: <IconWrapper icon={FaChartBar} className="menu-icon" />, text: 'Reports', roles: ['MD', 'TD', 'Project Manager', 'Accountant', 'Procurement'] },
      { path: '/users', icon: <IconWrapper icon={FaUsers} className="menu-icon" />, text: 'Staff Management', roles: ['MD', 'TD'] },
      { path: '/office', icon: <IconWrapper icon={FaBuilding} className="menu-icon" />, text: 'Office Management', roles: ['MD', 'TD', 'Accountant'] },
      { path: '/notifications', icon: <IconWrapper icon={FaBell} className="menu-icon" />, text: 'Notifications', roles: ['MD', 'TD', 'Project Manager', 'Accountant', 'Cashier', 'Store-Keeper (Logistics)', 'Procurement', 'Supervisor'] },
      { path: '/settings', icon: <IconWrapper icon={FaCog} className="menu-icon" />, text: 'Settings', roles: ['MD', 'TD'] },
      { path: '/logs', icon: <IconWrapper icon={FaClipboardList} className="menu-icon" />, text: 'Logs', roles: ['MD', 'TD'] },
    ];

    if (!user) return menuItems;
    
    // If admin@example.com, show all modules
    if (user.email === 'admin@example.com') return menuItems;
    
    // Filter menu items based on user role
    return menuItems.filter(item => item.roles.includes(user.role));
  };

  const handleLinkClick = () => {
    if (onLinkClick) {
      onLinkClick();
    }
  };

  return (
          <SidebarContainer $isOpen={isOpen}>
              <SidebarHeader $isOpen={isOpen}>
        <LogoContainer isOpen={isOpen}>
          <Logo>
            {!logoError ? (
              <LogoImage 
                src="/pfrs.jpeg" 
                alt="PFRS Logo" 
                onError={handleLogoError}
              />
            ) : (
              <LogoFallback>P</LogoFallback>
            )}
          </Logo>
          <span className="logo-text">PFRS</span>
        </LogoContainer>
        <ToggleButton onClick={toggleSidebar}>
          {isOpen ? <IconWrapper icon={FaChevronLeft} /> : <IconWrapper icon={FaChevronRight} />}
        </ToggleButton>
      </SidebarHeader>
      
      <SidebarMenu>
        {getMenuItems().map((item, index) => (
          <SidebarMenuItem key={index} isActive={isActive(item.path)} isOpen={isOpen}>
            <Link to={item.path} onClick={handleLinkClick}>
              {item.icon}
              <span className="menu-text">{item.text}</span>
            </Link>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
      
      <SidebarFooter isOpen={isOpen}>
        <div className="developer-info">
          <div className="developer-name">Developed by Sandfish</div>
          <div className="developer-tagline">Building Digital Solutions</div>
          <div className="copyright">
            &copy; {new Date().getFullYear()} PFRS
          </div>
        </div>
      </SidebarFooter>
    </SidebarContainer>
  );
};

export default Sidebar; 